"use client";

import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";
import { useEffect, useState } from "react";

const TESTIMONIALS = [
  {
    id: 1,
    quote:
      "A very practical, honest place to find true freedom and victory! God is front and centre and the very reason so many find recovery.",
    name: "Tim Batch",
    avatar: "/images/testimonials/Mountains_279_Mount_Fitz_Roy_1kjqqu.webp",
  },
  {
    id: 2,
    quote:
      "Hopeless, frustrated and alone. I found a better road, that gave me freedom that I have never known.",
    name: "Mari-Louise Erasmus",
    avatar:
      "/images/testimonials/Bridges__Castles___Palaces_652_Oct_vio_Frias_de_Oliveira_Bridge_x5oo35.webp",
  },
  {
    id: 3,
    quote:
      "Recovery has taught me about realness, honesty and vulnerability in a way that no other space has. Redemption is freely on offer for anyone who will grab it with both hands. God's grace is truly a gift!",
    name: "Debbie Austwick",
    avatar:
      "/images/testimonials/Bridges__Castles___Palaces_656_Himeji_Castle_6xg04i.webp",
  },
  {
    id: 4,
    quote:
      "Recovery has changed the way I live this life I have been blessed with. I am finally present and accounted for, not only for myself but for those around me. Recovery has helped me heal, it has helped me to forgive and it has shown me the magnitude of God's love.",
    name: "Bernice Nicholson",
    avatar:
      "/images/testimonials/Countryside_Scenes_207_Tree_swing_8wzsxx.webp",
  },
  {
    id: 5,
    quote:
      "Addiction brought me to my knees. A total loss of literally everything. Then recovery came along, how sweet it is to see fruit growing from what was once a dead tree, all glory to God!!!",
    name: "Brenden Gounden",
    avatar: "/images/testimonials/Countryside_Scenes_209_Plains_8un4mm.webp",
  },
] as const;

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 80 : -80,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -80 : 80,
    opacity: 0,
  }),
};

export function TestimonialsSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) {
      return;
    }
    const interval = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isPaused]);

  const goToNext = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % TESTIMONIALS.length);
  };

  const goToPrevious = () => {
    setDirection(-1);
    setCurrentIndex(
      (prev) => (prev - 1 + TESTIMONIALS.length) % TESTIMONIALS.length
    );
  };

  const goToSlide = (index: number) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };

  const current = TESTIMONIALS[currentIndex] ?? TESTIMONIALS[0];

  return (
    <section className="relative w-full bg-background">
      {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: pause-on-hover is a pointer-only progressive enhancement; keyboard/AT users are unaffected */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: same rationale */}
      <div
        className="mx-auto flex w-full max-w-7xl flex-col items-center gap-12 px-4 py-16 sm:px-6 md:gap-20 md:py-28 lg:px-8"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Carousel */}
        <div className="relative flex w-full items-center justify-center">
          {/* Previous arrow */}
          <button
            aria-label="Previous testimonial"
            className="absolute left-0 z-10 hidden rounded-full border border-border bg-background p-3 shadow-sm transition-colors hover:bg-muted md:flex"
            onClick={goToPrevious}
            type="button"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          {/* Slide content */}
          <div className="w-full max-w-[768px] overflow-x-clip px-4 md:px-16">
            {/* Grid stack: sizer + animated slide share one cell so height
                stays pinned to the tallest testimonial regardless of which
                slide is active or what viewport width is. */}
            <div className="grid grid-cols-1">
              {/* Invisible sizer — all testimonials stacked to claim max height */}
              <div
                aria-hidden="true"
                className="invisible col-start-1 row-start-1 grid grid-cols-1"
              >
                {TESTIMONIALS.map((t) => (
                  <div
                    className="col-start-1 row-start-1 flex flex-col items-center gap-6 md:gap-8"
                    key={t.id}
                  >
                    <blockquote className="body-md md:body-lg text-center font-semibold leading-[175%]">
                      "{t.quote}"
                    </blockquote>
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 shrink-0 sm:h-14 sm:w-14" />
                      <span className="body-base font-medium">{t.name}</span>
                    </div>
                  </div>
                ))}
              </div>

              <AnimatePresence custom={direction} initial={false} mode="wait">
                <motion.div
                  animate="center"
                  className="col-start-1 row-start-1 flex flex-col items-center gap-6 md:gap-8"
                  custom={direction}
                  exit="exit"
                  initial="enter"
                  key={current.id}
                  style={{ willChange: "transform, opacity" }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  variants={slideVariants}
                >
                  <blockquote className="body-md md:body-lg text-center font-semibold leading-[175%]">
                    "{current.quote}"
                  </blockquote>

                  <div className="flex items-center gap-4">
                    <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-full sm:h-14 sm:w-14">
                      <Image
                        alt={current.name}
                        className="object-cover"
                        fill
                        sizes="56px"
                        src={current.avatar}
                      />
                    </div>
                    <span className="body-base font-medium">
                      {current.name}
                    </span>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Next arrow */}
          <button
            aria-label="Next testimonial"
            className="absolute right-0 z-10 hidden rounded-full border border-border bg-background p-3 shadow-sm transition-colors hover:bg-muted md:flex"
            onClick={goToNext}
            type="button"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2">
          {TESTIMONIALS.map((t, index) => (
            <button
              aria-label={`Go to testimonial ${index + 1}`}
              className={`h-2 w-2 rounded-full bg-foreground transition-opacity ${
                index === currentIndex ? "opacity-100" : "opacity-20"
              }`}
              key={t.id}
              onClick={() => goToSlide(index)}
              type="button"
            />
          ))}
        </div>
      </div>
    </section>
  );
}
